"use client";

import { memo, useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Search, Trash2 } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { StudioPost } from "@prisma/client";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "ui";

import { trpc } from "@/lib/trpc/client";
import PostThumbnail from "@/components/post-thumbnail";

const DEFAULT_VISIBLE = 20;

const SortablePostRow = memo(function SortablePostRow({
  post,
  sortable,
  busy,
  onRemove,
}: {
  post: StudioPost;
  sortable: boolean;
  busy: boolean;
  onRemove: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: post.id, disabled: busy || !sortable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={
        "flex items-center gap-2 rounded-lg border border-border bg-card p-2" +
        (isDragging ? " relative z-10 opacity-70 shadow-lg" : "")
      }
    >
      {sortable && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={busy}
          aria-label={`Reorder ${post.title ?? "post"}`}
          aria-roledescription="Draggable post. Press space or enter to pick up, arrow keys to move, then space or enter to drop."
          className="cursor-grab touch-none rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing disabled:opacity-50"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div className="relative h-9 w-14 shrink-0 overflow-hidden rounded">
        <PostThumbnail
          post={post}
          alt={post.title ?? "Post"}
          sizes="56px"
          className="object-cover"
        />
      </div>
      <span className="flex-1 truncate text-sm text-foreground">
        {post.title ?? "Untitled"}
      </span>
      <button
        type="button"
        onClick={() => onRemove(post.id)}
        disabled={busy}
        aria-label="Remove from collection"
        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-destructive disabled:opacity-50"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
});

export default function CollectionPostsManager({
  collectionId,
  studioPosts,
  collectionPosts = [],
  initialPostIds,
  onChanged,
}: {
  collectionId: string;
  studioPosts: StudioPost[];
  // Posts already attached to this collection, draft-inclusive — a post can
  // be unpublished after being curated in, and should still render as "in
  // collection" even though `studioPosts` (the add-picker pool) is
  // published-only.
  collectionPosts?: StudioPost[];
  initialPostIds: string[];
  onChanged?: () => void;
}) {
  const [postIds, setPostIds] = useState<string[]>(initialPostIds);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const utils = trpc.useUtils();
  const updatePosts = trpc.collections.updatePosts.useMutation();
  const reorderPosts = trpc.collections.reorderPosts.useMutation();

  const byId = useMemo(() => {
    const map = new Map(collectionPosts.map((p) => [p.id, p]));
    for (const p of studioPosts) map.set(p.id, p);
    return map;
  }, [studioPosts, collectionPosts]);

  const currentPosts = postIds
    .map((id) => byId.get(id))
    .filter((p): p is StudioPost => Boolean(p));

  const addable = useMemo(() => {
    const notIn = studioPosts.filter((p) => !postIds.includes(p.id));
    const sorted = [...notIn].sort((a, b) => {
      const da = new Date(a.publishedAt || a.createdAt).getTime();
      const db = new Date(b.publishedAt || b.createdAt).getTime();
      return db - da;
    });
    const q = query.trim().toLowerCase();
    if (!q) return sorted.slice(0, DEFAULT_VISIBLE);
    return sorted.filter((p) => (p.title ?? "").toLowerCase().includes(q));
  }, [studioPosts, postIds, query]);

  const persist = async (
    nextIds: string[],
    revertIds: string[],
    errMsg: string,
  ) => {
    setBusy(true);
    setPostIds(nextIds);
    try {
      await updatePosts.mutateAsync({ collectionId, postIds: nextIds });
      // Keep the modal's next open fresh (get respects the 30s staleTime).
      await utils.collections.get.invalidate({ id: collectionId });
      onChanged?.();
    } catch {
      setPostIds(revertIds);
      toast.error(errMsg);
    } finally {
      setBusy(false);
    }
  };

  const addPost = (id: string) =>
    persist([...postIds, id], postIds, "Failed to add post");
  const removePost = useCallback(
    (id: string) =>
      persist(
        postIds.filter((x) => x !== id),
        postIds,
        "Failed to remove post",
      ),
    // persist is recreated every render; postIds is its only real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postIds, collectionId],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Save-on-drop, optimistic: the list reflects the new order immediately and
  // reverts with a toast if the mutation fails — same contract as add/remove.
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const from = postIds.indexOf(String(active.id));
      const to = postIds.indexOf(String(over.id));
      if (from === -1 || to === -1) return;

      const prev = postIds;
      const next = arrayMove(postIds, from, to);
      setBusy(true);
      setPostIds(next);
      try {
        await reorderPosts.mutateAsync({ collectionId, postIds: next });
        await utils.collections.get.invalidate({ id: collectionId });
        onChanged?.();
      } catch {
        setPostIds(prev);
        toast.error("Failed to reorder posts");
      } finally {
        setBusy(false);
      }
    },
    [postIds, collectionId, reorderPosts, utils, onChanged],
  );

  const sortable = currentPosts.length > 1;

  return (
    <div>
      <h3 className="mb-2 font-title text-sm font-semibold text-foreground">
        Posts
      </h3>

      {/* Add a post */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 w-full items-center gap-2 rounded-lg border border-input bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
          >
            <Search className="h-4 w-4" />
            <span>Add a post…</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          // Overlay the trigger (h-10) so its input replaces the "Add a post…"
          // button instead of rendering a second search field beneath it.
          sideOffset={-40}
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command shouldFilter={false} className="bg-popover">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search posts…"
            />
            <CommandList>
              <CommandEmpty>No posts found.</CommandEmpty>
              <CommandGroup>
                {addable.map((post) => (
                  <CommandItem
                    key={post.id}
                    value={post.id}
                    disabled={busy}
                    onSelect={() => addPost(post.id)}
                    className="flex items-center gap-3"
                  >
                    <div className="relative h-9 w-14 shrink-0 overflow-hidden rounded">
                      <PostThumbnail
                        post={post}
                        alt={post.title ?? "Post"}
                        sizes="56px"
                        className="object-cover"
                      />
                    </div>
                    <span className="truncate text-sm">
                      {post.title ?? "Untitled"}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Current posts */}
      <h4 className="mb-2 mt-4 text-xs font-medium text-muted-foreground">
        In this collection
      </h4>
      {currentPosts.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
          No posts yet — add some above.
        </p>
      ) : (
        <>
          {sortable && (
            <p className="mb-2 text-xs text-muted-foreground">
              Drag posts to change the order members see them in.
            </p>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={postIds}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-2">
                {currentPosts.map((post) => (
                  <SortablePostRow
                    key={post.id}
                    post={post}
                    sortable={sortable}
                    busy={busy}
                    onRemove={removePost}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </>
      )}
    </div>
  );
}
